import React, { useEffect, useState } from 'react';
import { businessPlanConfig, fundingConfig } from '../../commons/config';
import businessPlanFeatureImage from '../../assets/images/business-plan-feature.png';
import { DownloadOnAppStoreBanner } from '../../help/components/DownloadOnAppStoreBanner';
import { GetItOnGooglePlayBanner } from '../../help/components/GetItOnGooglePlayBanner';
import MobileAppFeatureImage from '../../assets/images/mobile-app-feature.png';
import AffiliateImage from '../../assets/images/affiliate.png';
import { ReleasePromptData } from '../types';
import { useIsSmallerThanDesktop } from '../../responsive/hooks/hooks';
import { openUrlInNewTab } from '../../util/window-utils';
import {
  APP_STORE_APP_URL,
  DownloadAppModal,
  GOOGLE_PLAY_APP_URL,
} from '../../help/components/DownloadAppModal';
import { v4 as uuidv4 } from 'uuid';
import { PersonalReferralLink } from '../../billing/components/RequestReferralLink';
import { useFetchedBillingCustomer } from '../../billing/hooks/useFetchedBillingCustomer';
import { useFetchedFunding } from '../../funding/hooks/useFetchedFunding';
import { FundingSearch } from '../../funding/components/FundingSearch';
import { AppRoute } from '../../router/AppRoutes';
import { useNavigate } from 'react-router-dom';

export const ReleaseContentBusinessPlan = () => (
  <div className="release-prompt__content-container">
    <img src={businessPlanFeatureImage} alt="Business plan" />
    <div>
      <div>
        Easily create a professional business plan to help secure funding for your small business.
        <br />
        Try our new AI-powered feature today!
      </div>
    </div>
  </div>
);

export const ReleaseContentMobileApp = () => {
  const isSmallerThanDesktop = useIsSmallerThanDesktop();
  const [downloadOnAppStoreKey, setDownloadOnAppStoreKey] = useState<string | null>(null);
  const [getOnPlayStoreKey, setGetOnPlayStoreKey] = useState<string | null>(null);

  return (
    <div className="release-prompt__content-container">
      <img src={MobileAppFeatureImage} alt="Mobile app" width={300} />
      <div
        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', rowGap: '24px' }}
      >
        <DownloadOnAppStoreBanner
          onClick={() => {
            if (isSmallerThanDesktop) {
              openUrlInNewTab(APP_STORE_APP_URL);
            } else {
              setDownloadOnAppStoreKey(uuidv4());
            }
          }}
          large={true}
        />
        <GetItOnGooglePlayBanner
          onClick={() => {
            if (isSmallerThanDesktop) {
              openUrlInNewTab(GOOGLE_PLAY_APP_URL);
            } else {
              setGetOnPlayStoreKey(uuidv4());
            }
          }}
          large={true}
        />
      </div>
      {downloadOnAppStoreKey && <DownloadAppModal store="app-store" key={downloadOnAppStoreKey} />}
      {getOnPlayStoreKey && <DownloadAppModal store="play-store" key={getOnPlayStoreKey} />}
    </div>
  );
};

export const ReleaseContentPersonalReferralLink = () => {
  const {
    query: { data: customer },
    queryStatusComponent: customerStatusComponent,
  } = useFetchedBillingCustomer();
  if (!customer) return customerStatusComponent;

  return (
    <div className="release-prompt__content-container flex-column">
      <PersonalReferralLink customer={customer} canHide={false} source="release-prompt" />
      <div>
        You can always check your referral link in the billing portal: Settings -&gt; Billing
      </div>
      <img src={AffiliateImage} alt="Affiliate" />
    </div>
  );
};

export const ReleaseContentFunding = (props: { onNavigate: () => void }) => {
  const { onNavigate } = props;
  const {
    query: { data: funding },
    queryStatusComponent: fundingStatusComponent,
  } = useFetchedFunding();
  const navigate = useNavigate();

  const isInFundingRoute = window.location.pathname === AppRoute.funding;
  useEffect(() => {
    if (isInFundingRoute) onNavigate();
  }, [isInFundingRoute, onNavigate]);

  if (isInFundingRoute) return null;
  if (!funding) return fundingStatusComponent;
  return (
    <FundingSearch
      funding={funding}
      hideHeader={true}
      onFind={() => {
        onNavigate();
        navigate(AppRoute.funding);
      }}
    />
  );
};

export const RELEASE_PROMPT_CODE_BUSINESS_PLAN = 'business-plan';
export const RELEASE_PROMPT_CODE_MOBILE_APP = 'mobile-app';
export const RELEASE_PROMPT_CODE_PERSONAL_REFERRAL_LINK = 'personal-referral-link';
export const RELEASE_PROMPT_CODE_FUNDING = 'funding';

export const RELEASE_PROMPTS: ReleasePromptData[] = [
  {
    route: AppRoute.businessPlan,
    enabled: businessPlanConfig.enabled,
    expiration: new Date('2025-03-01'),
    title: 'Our AI-Driven Business Plan Builder is Live!',
    promptId: 1,
    code: RELEASE_PROMPT_CODE_BUSINESS_PLAN,
  },
  {
    enabled: true,
    expiration: new Date('2025-04-01'),
    title: 'Growegy mobile app for Android and iOS is now available for download!',
    promptId: 2,
    code: RELEASE_PROMPT_CODE_MOBILE_APP,
    skipMobile: true,
  },
  {
    enabled: true,
    title: 'Invite to Growegy and Earn!',
    promptId: 3,
    code: RELEASE_PROMPT_CODE_PERSONAL_REFERRAL_LINK,
    skipMobile: true,
  },
  {
    enabled: fundingConfig.enabled,
    expiration: new Date('2025-10-31'),
    title: 'Find the right funding for you!',
    promptId: 4,
    code: RELEASE_PROMPT_CODE_FUNDING,
    skipMobile: false,
  },
];
